import AllocationDropdown from './AllocationDropdown'
import { DerivedValueBox, EditableValueBox } from './ValueBox'
import { formatMoneyCents, formatScheduleDate, formatSchedulePayment } from './format'
import { PAY_SCHED_MAX_SLOTS, parseScheduleSlot } from './scenarioNumbers'

// Screen-reader text for a row's grey calculated payment (our copy).
export const PAYMENT_COMPUTED_DESCRIPTION = 'Calculated payment'

const toCents = (n) => Math.round(n * 100)

// Letter 101 (8.3): Pre-Draw to Post-Draw, workbook E13..E14, lower first -- either can be the
// smaller one. `null` when compute has not sent both, or both are 0 (no loan amount or rate).
export function paymentRange(computed) {
  const pre = computed?.pre_draw_payment
  const post = computed?.post_draw_payment
  if (pre == null || post == null || !Number.isFinite(Number(pre)) || !Number.isFinite(Number(post))) return null
  if (Number(pre) === 0 && Number(post) === 0) return null
  return { lower: Math.min(Number(pre), Number(post)), upper: Math.max(Number(pre), Number(post)) }
}

// Spec 4.2.1 row 2: `2,354.63 - 2,690.04`, no $ -- kept so under letter 111 item 6, README.md ("Money
// boxes show $xxx,xxx"). One figure when both ends show the same cents.
export function formatPaymentRange(range) {
  if (!range) return null
  const lower = formatSchedulePayment(range.lower)
  const upper = formatSchedulePayment(range.upper)
  return lower === upper ? lower : `${lower} - ${upper}`
}

// Spec 4.4 verbatim, broken where the design breaks it: `Enter a payment between` / `$2,354.63 and $2,690.04.`
export function paymentRangeMessage(range) {
  return `Enter a payment between\n$${formatSchedulePayment(range.lower)} and $${formatSchedulePayment(range.upper)}.`
}

// Cong, letter 104 Response 1: the first and last rows (part months) are never checked; nor is a row
// without a typed override. Compared in cents, as displayed, so a figure equal to a shown bound passes.
export function isPaymentOutOfRange(range, index, rowCount, override) {
  if (!range || index <= 0 || index >= rowCount - 1) return false
  if (!Number.isFinite(override) || override === 0) return false
  const cents = toCents(override)
  return cents < toCents(range.lower) || cents > toCents(range.upper)
}

// Panel spec 4.2.1. Row count is `schedule.length`, never the storage array's own 24 slots: row 0 is
// Prepaid (purchase date), then one payment per month dated the 1st, then the sale date when the sale
// is not on the 1st -- README.md ("the payment panel's rows"). A slot of 0 is "no override", so the
// box shows the row's calculated baseline + proration in grey instead. While a recompute is out the
// last figures stay (letter 104, bug 2): a row's value never includes its override, so it cannot echo a typed number.
export default function ProjectedInterestPanel({ scenario, computed, onScheduleChange, onFieldChange }) {
  const schedule = computed?.interestSchedule ?? []
  const prorations = scenario?.paySchedProrations ?? []
  const range = paymentRange(computed)
  const rangeMessage = range ? paymentRangeMessage(range) : null

  return (
    <div className="pt-[8px] flex flex-col">
      <div className="flex flex-wrap items-center min-h-[32px] gap-y-[4px]">
        {/* Letter 104 (1.3): "Allocation -> Interest Payments". Still the sell_tax_applied control. */}
        <span className="text-[14px] font-normal text-brand-dark">Interest Payments</span>
        {/* Spec 4.2.1: the 144-wide pill sits inside an invisible 160-wide box, 8px inset on both
            sides (5108-5100=8, 5260-5252=8) -- centering a 144-wide child in a 160-wide box gives
            that same 8px symmetrically, without hardcoding a padding value that has to match by
            hand. Without this wrapper the pill flushes straight to the row's own right edge. */}
        <div className="ml-auto w-[160px] flex justify-center">
          <AllocationDropdown
            value={scenario?.sell_tax_applied ?? 'hold'}
            onChange={(v) => onFieldChange('sell_tax_applied', v)}
          />
        </div>
      </div>

      <div className="mt-[8px] flex flex-wrap items-center min-h-[32px] gap-y-[4px]">
        <span className="text-[14px] font-normal text-brand-dark">Payment Range</span>
        <span className="ml-auto">
          <DerivedValueBox value={formatPaymentRange(range)} width={139} align="left" ariaLabel="Payment Range" />
        </span>
      </div>

      <div className="mt-[8px] flex flex-wrap items-center min-h-[32px] gap-y-[4px]">
        <span className="text-[14px] font-semibold text-brand-secondary">Date</span>
        <span className="ml-auto w-[88px] text-right text-[14px] font-semibold text-brand-secondary">Payment</span>
      </div>

      {schedule.map((row, i) => {
        const dateLabel = formatScheduleDate(row.date)
        const override = Number(prorations[i])
        const overridden = Number.isFinite(override) && override !== 0
        const calculated = formatMoneyCents((Number(row.baseline) || 0) + (Number(row.proration) || 0))
        // A warning only: the typed figure is still stored and still counted in Projected Interest.
        // Parsed as the slot stores it, so the live (typed) check and the stored one agree.
        const validate = (text) => (isPaymentOutOfRange(range, i, schedule.length, parseScheduleSlot(text)) ? rangeMessage : null)
        return (
          <div key={i} className="mt-[8px] flex flex-wrap items-center min-h-[32px] gap-y-[4px]">
            {dateLabel != null ? (
              <span className="text-[14px] font-normal text-brand-dark">{dateLabel}</span>
            ) : (
              <span className="text-[14px] font-normal text-brand-placeholder">{'—'}</span>
            )}
            <span className="ml-auto">
              <EditableValueBox money cents
                value={overridden ? String(prorations[i]) : ''}
                onChange={(v) => onScheduleChange(i, v)}
                placeholder={overridden ? null : calculated}
                placeholderDescription={PAYMENT_COMPUTED_DESCRIPTION}
                validate={validate}
                // The validator stores at most PAY_SCHED_MAX_SLOTS overrides; a row past them shows its value only.
                disabled={i >= PAY_SCHED_MAX_SLOTS}
                ariaLabel={`Payment ${i + 1}`}
              />
            </span>
          </div>
        )
      })}
    </div>
  )
}
